import { useCallback, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { AnefiDocumentType } from "@anefi/types";
import { useInvestForm } from "@anefi/contexts";
import { useFetchUser, usePutUser } from "@anefi/services";

import useModals from "contexts/modals/modals.hooks";
import { getLegalStepOneSchema } from "utils/form.utils";
import { getLegalStepThreeSchema } from "utils/form.utils";
import { getLegalStepTwoSchema } from "utils/form.utils";
import { StepTwoLegalFormValues } from "../LegalPath/StepTwoLegalForm/StepTwoLegalForm.types";
import { StepOneLegalFormValues } from "../LegalPath/StepOneLegalForm/StepOneLegalForm.types";
import { StepThreeLegalFormValues } from "../LegalPath/StepThreeLegalForm/StepThreeLegalForm.types";
import useAuth from "contexts/auth/auth.context.hooks";
import { transformValuesToRequestLegal } from "./useCheckoutLegalForms.helpers";
import { createSuccessNotification } from "utils/notifications.utils";

const useCheckoutLegalForms = () => {
  const { setShowFirstInvestmentModal } = useModals();
  const [currentStep, setCurrentStep] = useState(1);
  const [setDocumentCode] = useState<AnefiDocumentType>();
  const { legalInvestForm, setLegalInvestForm } = useInvestForm();
  const { resetInvestFormContext } = useInvestForm();
  const auth = useAuth();
  const { data: user } = useFetchUser(auth);
  const { mutate: putUserMutate, reset: putUserReset } = usePutUser(auth);
  const { additionalInfo } = user ?? {};

  const nextStep = useCallback(async () => {
    setCurrentStep(currentStep + 1);
  }, [currentStep]);

  const onClose = useCallback(async () => {
    setShowFirstInvestmentModal(false);
  }, [setShowFirstInvestmentModal]);

  const stepOneFormMethods = useForm<StepOneLegalFormValues>({
    mode: "onChange",
    resolver: yupResolver(getLegalStepOneSchema()),
    defaultValues: {
      documentTypeCode: "2",
      document: user?.document,
      businessName: additionalInfo?.businessName,
      businessActivityTypeCode: additionalInfo?.businessActivityTypeCode,
      nationalityCountryCode: additionalInfo?.nationalityCountryCode || "",
      phone: user?.phone?.number,
      startDate: additionalInfo?.startDate
    }
  });
  const { handleSubmit: handleStepOneSubmit } = stepOneFormMethods;

  const stepTwoFormMethods = useForm<StepTwoLegalFormValues>({
    mode: "onChange",
    resolver: yupResolver(getLegalStepTwoSchema()),
    defaultValues: {
      homeAddressProvinceCode: additionalInfo?.homeAddressProvinceCode || "",
      orgGeoCodeWorkAddress: additionalInfo?.orgGeoCodeWorkAddress || "",
      countryCodeWorkAddress: additionalInfo?.countryCodeWorkAddress ?? "001",
      mainStreetWorkAddress: additionalInfo?.mainStreetWorkAddress,
      numberWorkAddress: additionalInfo?.numberWorkAddress,
      intersectionWorkAddress: additionalInfo?.intersectionWorkAddress,
      // @ts-ignore TODO: correctly type the user with the additional Phone
      additionalPhone: user?.additionalPhone?.number
    }
  });
  const { handleSubmit: handleStepTwoSubmit } = stepTwoFormMethods;

  const stepThreeFormMethods = useForm<StepThreeLegalFormValues>({
    mode: "onChange",
    resolver: yupResolver(getLegalStepThreeSchema()),
    defaultValues: {
      totalAssets: additionalInfo?.totalAssets || "",
      totalLiabilities: additionalInfo?.totalLiabilities || "",
      totalIncome: additionalInfo?.totalIncome || "",
      totalExpenditure: additionalInfo?.totalExpenditure || ""
    }
  });
  const { handleSubmit: handleStepThreeSubmit } = stepThreeFormMethods;

  const submitStepOneFormHandler = useCallback<
    SubmitHandler<StepOneLegalFormValues>
  >(
    async (values: StepOneLegalFormValues) => {
      setLegalInvestForm(form => {
        return { ...form, ...values };
      });
      nextStep();
    },
    [nextStep, setLegalInvestForm]
  );

  const submitStepTwoFormHandler = useCallback<
    SubmitHandler<StepTwoLegalFormValues>
  >(
    async (values: StepTwoLegalFormValues) => {
      setLegalInvestForm(form => {
        return { ...form, ...values };
      });
      nextStep();
    },
    [nextStep, setLegalInvestForm]
  );

  const submitStepThreeFormHandler = useCallback<
    SubmitHandler<StepThreeLegalFormValues>
  >(
    async (values: StepThreeLegalFormValues) => {
      const userFormValues = transformValuesToRequestLegal({
        ...legalInvestForm,
        ...values
      });

      const newValues = {
        ...user,
        ...userFormValues,
        active: !!user?.active,
        additionalInfo: {
          ...user?.additionalInfo,
          ...userFormValues?.additionalInfo
        },
        country: {
          id: 1,
          name: "ECU"
        }
      };

      await putUserMutate(newValues);
      onClose();
      createSuccessNotification(
        "Guardar información",
        "Has guardado tu información correctamente.",
        4000
      );
      putUserReset();
      resetInvestFormContext();
    },
    [
      legalInvestForm,
      onClose,
      putUserMutate,
      putUserReset,
      resetInvestFormContext,
      user
    ]
  );

  const handleSubmit = useCallback(() => {
    switch (currentStep) {
      case 1:
        handleStepOneSubmit(submitStepOneFormHandler)();
        break;
      case 2:
        handleStepTwoSubmit(submitStepTwoFormHandler)();
        break;
      case 3:
        handleStepThreeSubmit(submitStepThreeFormHandler)();
        break;

      default:
        break;
    }
  }, [
    currentStep,
    handleStepOneSubmit,
    handleStepThreeSubmit,
    handleStepTwoSubmit,
    submitStepOneFormHandler,
    submitStepThreeFormHandler,
    submitStepTwoFormHandler
  ]);

  return useMemo(
    () => ({
      handleSubmit,
      currentStep,
      setCurrentStep,
      onClose,
      stepOneFormMethods,
      stepTwoFormMethods,
      stepThreeFormMethods,
      setDocumentCode
    }),
    [
      handleSubmit,
      currentStep,
      onClose,
      stepOneFormMethods,
      stepTwoFormMethods,
      stepThreeFormMethods,
      setDocumentCode
    ]
  );
};

export default useCheckoutLegalForms;
